import type { WidgetMountRoot } from "../../core/dom/widget-root.ts";
import type { RuntimeWidgetConfig } from "../../core/config/public-config.types.ts";
import {
  createFeaturePanelContent,
  type RenderFeatureControlInput,
} from "../features/feature-panel.ts";
import { createFeatureRuntime } from "../features/feature-runtime.ts";
import {
  runtimeFeatureRegistry,
  type RuntimeFeatureModule,
} from "../features/runtime-feature-registry.ts";
import { createLauncherButton } from "../launcher/launcher.ts";
import { createPanel, setPanelOpen } from "../panel/panel.ts";
import {
  createPreferenceStorage,
  type PreferenceValues,
} from "../preferences/preference-storage.ts";

function countActivePreferences(preferences: PreferenceValues) {
  return Object.values(preferences).filter((value) => {
    if (value === false || value === null || value === undefined || value === "") {
      return false;
    }

    if (value === "off" || value === 0) {
      return false;
    }

    return true;
  }).length;
}

const shellStyles = `
:host,
.migrosone-a11y-widget-shell {
  all: initial;
  color-scheme: light;
  font-family: Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-button {
  display: inline-grid;
  place-items: center;
  position: fixed;
  z-index: 2147483000;
  width: 56px;
  height: 56px;
  border: 0;
  border-radius: 28px;
  padding: 0;
  color: #ffffff;
  background: #1f6f4a;
  box-shadow: 0 8px 24px rgb(23 32 42 / 20%);
  cursor: pointer;
}

.migrosone-a11y-widget-button__icon,
.migrosone-a11y-widget-button__icon svg {
  display: block;
  width: 30px;
  height: 30px;
}

.migrosone-a11y-widget-button[data-position="bottom-right"] {
  right: max(24px, env(safe-area-inset-right));
  bottom: max(24px, env(safe-area-inset-bottom));
}

.migrosone-a11y-widget-button[data-position="bottom-left"] {
  left: max(24px, env(safe-area-inset-left));
  bottom: max(24px, env(safe-area-inset-bottom));
}

.migrosone-a11y-widget-button[data-position="top-right"] {
  right: max(24px, env(safe-area-inset-right));
  top: max(24px, env(safe-area-inset-top));
}

.migrosone-a11y-widget-button[data-position="top-left"] {
  left: max(24px, env(safe-area-inset-left));
  top: max(24px, env(safe-area-inset-top));
}

.migrosone-a11y-widget-panel {
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 2147483001;
  box-sizing: border-box;
  width: min(445px, calc(100vw - 16px));
  padding: 0;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d8dfe3;
  box-shadow: 0 18px 48px rgb(23 32 42 / 24%);
  font: 400 16px/1.45 Arial, Helvetica, sans-serif;
  overflow-y: auto;
}

.migrosone-a11y-widget-panel[hidden] {
  display: none;
}

.migrosone-a11y-widget-panel[data-side="right"] {
  right: 0;
  border-radius: 12px 0 0 12px;
}

.migrosone-a11y-widget-panel[data-side="left"] {
  left: 0;
  border-radius: 0 12px 12px 0;
}

.migrosone-a11y-widget-panel__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 36px;
  gap: 14px;
  padding: 18px 16px 16px;
  border-bottom: 1px solid #d8dfe3;
}

.migrosone-a11y-widget-panel__heading {
  min-width: 0;
}

.migrosone-a11y-widget-panel__title {
  margin: 0;
  color: #0d141b;
  font: 800 24px/1.16 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-panel__subtitle {
  margin: 6px 0 0;
  color: #5d6670;
  font: 400 15px/1.3 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-panel__actions {
  display: flex;
  grid-column: 1 / -1;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 2px;
}

.migrosone-a11y-widget-panel__active-status {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 0 12px;
  color: #149036;
  background: #e9f6ec;
  font: 500 14px/1 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-panel__active-status::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #149036;
}

.migrosone-a11y-widget-panel__body {
  padding: 24px 16px 18px;
  color: #5d6670;
}

.migrosone-a11y-widget-features {
  display: grid;
  gap: 24px;
}

.migrosone-a11y-widget-features__category {
  display: grid;
  gap: 16px;
}

.migrosone-a11y-widget-features__heading {
  margin: 0;
  color: #5d6670;
  font: 800 14px/1.2 Arial, Helvetica, sans-serif;
  letter-spacing: 0.9px;
  text-transform: uppercase;
}

.migrosone-a11y-widget-features__card {
  overflow: hidden;
  border: 2px solid #d8dfe3;
  border-radius: 12px;
  background: #ffffff;
}

.migrosone-a11y-widget-features__card > .migrosone-a11y-widget-feature + .migrosone-a11y-widget-feature {
  border-top: 1px solid #d8dfe3;
}

.migrosone-a11y-widget-features__empty,
.migrosone-a11y-widget-feature__placeholder,
.migrosone-a11y-widget-feature__unavailable,
.migrosone-a11y-widget-panel__status {
  margin: 0;
  color: #5d6670;
  font: 400 15px/1.4 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-panel__status {
  min-height: 20px;
  padding: 0 16px 18px;
}

.migrosone-a11y-widget-feature {
  box-sizing: border-box;
  display: grid;
  min-height: 72px;
  grid-template-columns: 42px minmax(0, 1fr) minmax(144px, auto);
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: #0d141b;
  background: #ffffff;
  overflow-wrap: anywhere;
}

.migrosone-a11y-widget-feature__icon {
  display: inline-grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 10px;
  color: #0d141b;
  background: #f0f2f4;
}

.migrosone-a11y-widget-feature__icon svg {
  display: block;
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2.6;
}

.migrosone-a11y-widget-feature__copy {
  min-width: 0;
}

.migrosone-a11y-widget-feature__title {
  margin: 0;
  color: #0d141b;
  font: 800 16px/1.18 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-feature__description {
  margin: 3px 0 0;
  color: #5d6670;
  font: 400 13px/1.28 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-feature__meta {
  display: none;
}

.migrosone-a11y-widget-feature__control {
  display: flex;
  min-width: 0;
  justify-content: flex-end;
}

.migrosone-a11y-widget-feature-control {
  display: flex;
  width: auto;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  color: #0d141b;
  font: 700 15px/1.2 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-feature-control > label,
.migrosone-a11y-widget-feature-control > span {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.migrosone-a11y-widget-feature-control select {
  box-sizing: border-box;
  min-width: 152px;
  min-height: 44px;
  border: 2px solid #d8dfe3;
  border-radius: 8px;
  padding: 0 12px;
  color: #0d141b;
  background: #ffffff;
  font: 700 15px/1 Arial, Helvetica, sans-serif;
  cursor: pointer;
}

.migrosone-a11y-widget-feature-segmented {
  box-sizing: border-box;
  display: grid;
  min-width: 176px;
  min-height: 44px;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  overflow: hidden;
  border: 2px solid #d8dfe3;
  border-radius: 8px;
  background: #edf1f3;
}

.migrosone-a11y-widget-feature-segmented__button {
  min-width: 0;
  border: 0;
  border-left: 1px solid #d8dfe3;
  color: #5d6670;
  background: transparent;
  font: 700 13px/1 Arial, Helvetica, sans-serif;
  cursor: pointer;
}

.migrosone-a11y-widget-feature-segmented__button:first-child {
  border-left: 0;
}

.migrosone-a11y-widget-feature-segmented__button--selected {
  margin: 3px;
  border-left: 0;
  border-radius: 6px;
  color: #0d141b;
  background: #ffffff;
  box-shadow: 0 2px 8px rgb(17 24 39 / 14%);
}

.migrosone-a11y-widget-feature-stepper {
  display: grid;
  width: 156px;
  min-height: 44px;
  grid-template-columns: 42px minmax(72px, 1fr) 42px;
  align-items: center;
  overflow: hidden;
  border: 2px solid #d8dfe3;
  border-radius: 8px;
  background: #ffffff;
}

.migrosone-a11y-widget-feature-stepper__button {
  box-sizing: border-box;
  width: 42px;
  height: 44px;
  border: 0;
  color: #0d141b;
  background: #ffffff;
  font: 400 22px/1 Arial, Helvetica, sans-serif;
  cursor: pointer;
}

.migrosone-a11y-widget-feature-stepper__button + .migrosone-a11y-widget-feature-stepper__value,
.migrosone-a11y-widget-feature-stepper__value + .migrosone-a11y-widget-feature-stepper__button {
  border-left: 2px solid #d8dfe3;
}

.migrosone-a11y-widget-feature-stepper__button:disabled {
  cursor: not-allowed;
  color: #a6adb5;
  background: #ffffff;
}

.migrosone-a11y-widget-feature-stepper__value {
  display: inline-grid;
  height: 44px;
  min-width: 0;
  place-items: center;
  color: #0d141b;
  background: #ffffff;
  font: 800 16px/1 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-feature input[type="checkbox"] {
  position: relative;
  box-sizing: border-box;
  width: 56px;
  height: 34px;
  flex: 0 0 auto;
  border: 2px solid #d8dfe3;
  border-radius: 999px;
  appearance: none;
  background: #edf1f3;
  box-shadow: inset 0 0 0 2px rgb(17 24 39 / 3%);
  cursor: pointer;
}

.migrosone-a11y-widget-feature input[type="checkbox"]::before {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgb(17 24 39 / 18%);
  transition: transform 120ms ease;
}

.migrosone-a11y-widget-feature input[type="checkbox"]:checked {
  border-color: #149036;
  background: #149036;
}

.migrosone-a11y-widget-feature input[type="checkbox"]:checked::before {
  transform: translateX(22px);
}

.migrosone-a11y-widget-panel__close,
.migrosone-a11y-widget-panel__reset {
  border: 2px solid #d8dfe3;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
}

.migrosone-a11y-widget-panel__close {
  display: inline-grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border-color: transparent;
  color: #5d6670;
  font: 400 32px/1 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-panel__reset {
  min-height: 44px;
  margin-top: 0;
  padding: 0 14px;
  color: #0d141b;
  font: 800 15px/1 Arial, Helvetica, sans-serif;
}

.migrosone-a11y-widget-button:focus-visible,
.migrosone-a11y-widget-panel__close:focus-visible,
.migrosone-a11y-widget-panel__reset:focus-visible,
.migrosone-a11y-widget-feature-segmented__button:focus-visible,
.migrosone-a11y-widget-feature-stepper__button:focus-visible,
.migrosone-a11y-widget-feature input:focus-visible,
.migrosone-a11y-widget-feature select:focus-visible {
  outline: 4px solid #f7c948;
  outline-offset: 4px;
}

@media (max-width: 720px) {
  .migrosone-a11y-widget-panel {
    width: 100vw;
    border-radius: 0;
  }

  .migrosone-a11y-widget-panel__header,
  .migrosone-a11y-widget-panel__body,
  .migrosone-a11y-widget-panel__status {
    padding-left: 20px;
    padding-right: 20px;
  }

  .migrosone-a11y-widget-panel__title {
    font-size: 26px;
  }

  .migrosone-a11y-widget-panel__subtitle {
    font-size: 15px;
  }

  .migrosone-a11y-widget-panel__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .migrosone-a11y-widget-feature {
    min-height: 0;
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 10px;
    padding: 12px;
  }

  .migrosone-a11y-widget-feature__icon {
    width: 44px;
    height: 44px;
  }

  .migrosone-a11y-widget-feature__control {
    grid-column: 1 / -1;
    justify-content: stretch;
  }

  .migrosone-a11y-widget-feature__title {
    font-size: 17px;
  }

  .migrosone-a11y-widget-feature__description {
    font-size: 14px;
  }

  .migrosone-a11y-widget-feature-stepper,
  .migrosone-a11y-widget-feature-segmented,
  .migrosone-a11y-widget-feature-control select,
  .migrosone-a11y-widget-feature-control {
    width: 100%;
    min-width: 0;
  }
}
`;

export type MountWidgetShellInput = {
  config: RuntimeWidgetConfig;
  document?: Document;
  mountRoot: WidgetMountRoot;
  onReset?: () => void;
  renderControl?: (input: RenderFeatureControlInput) => HTMLElement;
  runtimeFeatureRegistry?: RuntimeFeatureModule[];
  storage?: Storage | null;
};

export type MountedWidgetShell = {
  destroy(): void;
};

export function mountWidgetShell({
  config,
  document = globalThis.document,
  mountRoot,
  onReset,
  renderControl,
  runtimeFeatureRegistry: featureRegistry = runtimeFeatureRegistry,
  storage,
}: MountWidgetShellInput): MountedWidgetShell {
  let isOpen = false;
  let preferences: PreferenceValues;
  const style = document.createElement("style");
  const preferenceStorage = createPreferenceStorage({ config, storage });
  preferences = preferenceStorage.read();
  const featureRuntime = createFeatureRuntime({
    config,
    document,
    preferences,
    registry: featureRegistry,
  });
  const renderPanelBody = () =>
    createFeaturePanelContent({
      availableFeatureKeys: featureRuntime.getRenderableFeatureKeys(),
      document,
      features: config.features,
      onPreferenceChange(key, nextValue) {
        preferences = {
          ...preferences,
          [key]: nextValue,
        };
        preferenceStorage.write(preferences);
        featureRuntime.applyFeature(key, nextValue);
        panel.setActivePreferenceCount(countActivePreferences(preferences));
      },
      preferences,
      renderControl: renderControl ?? featureRuntime.renderControl,
    });
  const setOpen = (open: boolean) => {
    isOpen = open;
    setPanelOpen({
      initialFocus: panel.closeButton,
      launcher,
      open,
      panel: panel.element,
    });
  };
  const launcher = createLauncherButton({
    document,
    onToggle: () => setOpen(!isOpen),
    position: config.widget.launcherPosition,
  });
  const panel = createPanel({
    activePreferenceCount: countActivePreferences(preferences),
    bodyContent: renderPanelBody(),
    document,
    onClose: () => setOpen(false),
    onReset: () => {
      preferences = {};
      const resetSucceeded = preferenceStorage.reset();
      featureRuntime.resetAll();
      featureRuntime.applyAll(preferences);
      panel.body.replaceChildren(renderPanelBody());
      panel.setActivePreferenceCount(0);
      onReset?.();
      return resetSucceeded;
    },
    side: config.widget.panelSide,
  });
  const onKeyDown = (event: KeyboardEvent) => {
    if (isOpen && event.key === "Escape") {
      setOpen(false);
    }
  };

  style.textContent = shellStyles;
  mountRoot.replaceChildren(style, launcher, panel.element);
  document.addEventListener("keydown", onKeyDown);
  featureRuntime.applyAll(preferences);

  return {
    destroy() {
      document.removeEventListener("keydown", onKeyDown);
      featureRuntime.resetAll();
      mountRoot.replaceChildren();
    },
  };
}
